import { useState, useEffect } from 'react';
import api from '../api';
import { MapPin, Users, Gift, Download, Loader2, ArrowUpDown } from 'lucide-react';

// Players who entered a geofence over a date range.
// Self-contained report (own filters) rendered as a tab inside Reports.jsx.

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return String(d); }
};
const toInput = (d) => d.toISOString().slice(0, 10);

const GeofenceReport = () => {
  const [loading, setLoading] = useState(false);
  const [geofences, setGeofences] = useState([]);
  const [groups, setGroups] = useState([]);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sort, setSort] = useState({ key: 'enteredAt', dir: 'desc' });

  const [filters, setFilters] = useState(() => {
    const end = new Date();
    const start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    return { start_date: toInput(start), end_date: toInput(end), geofence_id: '', group_id: '', event_type: 'all' };
  });

  useEffect(() => {
    api.get('/admin/geofence-locations').then((r) => setGeofences(r.data.locations || [])).catch(() => {});
    api.get('/admin/user-groups').then((r) => setGroups(r.data.groups || r.data || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const res = await api.get('/admin/reports/geofence-entries', { params });
      setData(res.data.data || []);
      setSummary(res.data.summary || null);
    } catch (e) {
      setData([]); setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // initial load

  const sortedData = [...data].sort((a, b) => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    const av = a[sort.key], bv = b[sort.key];
    if (av == null) return 1;
    if (bv == null) return -1;
    return av > bv ? dir : av < bv ? -dir : 0;
  });

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  const exportCsv = () => {
    const headers = ['Geofence', 'Player', 'Member #', 'Event', 'Time (PT)', 'Staff Notified', 'Player Notified', 'Reward Offered', 'Reward', 'Claimed'];
    const rows = sortedData.map((r) => [
      r.geofenceName, r.playerName, r.memberNumber ?? '', r.eventType, fmtDate(r.enteredAt),
      r.staffNotified ? 'yes' : 'no', r.notifiedPlayer ? 'yes' : 'no', r.rewardOffered ? 'yes' : 'no',
      r.rewardTitle ?? '', r.rewardClaimedAt ? fmtDate(r.rewardClaimedAt) : '',
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `geofence-entries-${filters.start_date}-to-${filters.end_date}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const Th = ({ k, children }) => (
    <th onClick={() => toggleSort(k)} className="px-3 py-2 text-left text-xs font-medium text-gray-400 cursor-pointer select-none hover:text-white">
      <span className="inline-flex items-center gap-1">{children}<ArrowUpDown size={12} /></span>
    </th>
  );

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Start date</label>
            <input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">End date</label>
            <input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Geofence</label>
            <select value={filters.geofence_id} onChange={(e) => setFilters({ ...filters, geofence_id: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm">
              <option value="">All geofences</option>
              {geofences.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Segment</label>
            <select value={filters.group_id} onChange={(e) => setFilters({ ...filters, group_id: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm">
              <option value="">All players</option>
              {groups.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Event</label>
            <select value={filters.event_type} onChange={(e) => setFilters({ ...filters, event_type: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm">
              <option value="all">Enter &amp; exit</option>
              <option value="enter">Enter only</option>
              <option value="exit">Exit only</option>
            </select>
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <button onClick={load} disabled={loading} className="px-4 py-2 bg-amber-500 text-black rounded hover:bg-amber-400 font-medium disabled:opacity-50 flex items-center gap-2">
            {loading ? <Loader2 size={16} className="animate-spin" /> : null} Run report
          </button>
          <button onClick={exportCsv} disabled={!data.length} className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-40 flex items-center gap-2">
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card icon={MapPin} label="Entries" value={summary.totalEntries} />
          <Card icon={Users} label="Unique players" value={summary.uniquePlayers} />
          <Card icon={Gift} label="Rewards offered" value={summary.rewardsOffered} />
          <Card icon={Gift} label="Rewards claimed" value={summary.rewardsClaimed} />
          <Card icon={Gift} label="Claim rate" value={`${summary.claimRate}%`} />
        </div>
      )}

      {/* By-geofence breakdown */}
      {summary?.byGeofence?.length > 1 && (
        <div className="bg-gray-800 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-white mb-2">By geofence</h3>
          <div className="flex flex-wrap gap-2">
            {summary.byGeofence.map((g) => (
              <span key={g.geofenceId} className="px-3 py-1.5 rounded bg-gray-700 text-sm text-gray-200">
                {g.geofenceName}: <strong>{g.entries}</strong> entries · {g.uniquePlayers} players
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50">
              <tr>
                <Th k="geofenceName">Geofence</Th>
                <Th k="playerName">Player</Th>
                <Th k="memberNumber">Member #</Th>
                <Th k="eventType">Event</Th>
                <Th k="enteredAt">Time (PT)</Th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Notified</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Reward</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {sortedData.map((r) => (
                <tr key={r.id} className="hover:bg-gray-700/30">
                  <td className="px-3 py-2 text-sm text-gray-200">{r.geofenceName}</td>
                  <td className="px-3 py-2 text-sm text-white">{r.playerName}</td>
                  <td className="px-3 py-2 text-sm text-gray-400">{r.memberNumber ?? '—'}</td>
                  <td className="px-3 py-2 text-sm">
                    <span className={`px-1.5 py-0.5 rounded text-xs ${r.eventType === 'enter' ? 'bg-green-500/20 text-green-400' : 'bg-gray-600 text-gray-300'}`}>{r.eventType}</span>
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-300">{fmtDate(r.enteredAt)}</td>
                  <td className="px-3 py-2 text-xs text-gray-400">
                    {r.staffNotified && <span className="text-amber-400 mr-1">staff</span>}
                    {r.notifiedPlayer && <span className="text-blue-400">player</span>}
                    {!r.staffNotified && !r.notifiedPlayer && '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.rewardTitle ? (
                      <span className={r.rewardClaimedAt ? 'text-green-400' : 'text-gray-300'}>
                        {r.rewardTitle}{r.rewardClaimedAt ? ' ✓ claimed' : ' (offered)'}
                      </span>
                    ) : '—'}
                  </td>
                </tr>
              ))}
              {!sortedData.length && !loading && (
                <tr><td colSpan={7} className="px-3 py-12 text-center text-gray-500">No entries for the selected filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Card = ({ icon: Icon, label, value }) => (
  <div className="bg-gray-800 rounded-xl p-4">
    <div className="flex items-center gap-2 text-gray-400 text-xs mb-1"><Icon size={14} />{label}</div>
    <div className="text-2xl font-bold text-white">{value ?? 0}</div>
  </div>
);

export default GeofenceReport;
